<template>
  <sTable
    :headers="tableHeaders"
    :items="designationTitles"
    style="width: 100%"
    aria-label="nonPositionTable"
    :loading="loading"
    class="w-full"
    has-number
  >
    <template v-slot:item="{ item }">
      <span v-if="item.title">
        {{ (item.data || item).title || "------" }}
      </span>
      <span v-else-if="item.jobClassification">
        {{
          (item.data || item).jobClassification
            ? (item.data || item).jobClassification.name
            : "------"
        }}
      </span>
      <span v-else-if="item.jobLevel">
        {{ (item.data || item).jobLevel ? (item.data || item).jobLevel.name : "------" }}
      </span>
      <span v-else-if="item.minimumJobLevel">
        {{ (item.data || item).minimumJobLevel ? (item.data || item).minimumJobLevel.name : "------" }}
      </span>
      <span v-else-if="item.jobGrade">
        {{ (item.data || item).jobGrade ? (item.data || item).jobGrade.name : "------" }}
      </span>
      <span v-else-if="item.minimumJobGrade">
        {{ (item.data || item).minimumJobGrade ? (item.data || item).minimumJobGrade.name : "------" }}
      </span>
      <span v-else-if="item.payScales">
        <div v-if="getFirstPayScale(item)">
          <span>{{ getPayScaleName(item) }}</span>
          <p class="text-carrotOrange text-xs">{{ getPayScaleAmount(item) }}</p>
        </div>
        <span v-else>------</span>
      </span>
      <span v-else-if="item.payGrades">
        <div v-if="getFirstPayGrade(item)">
          <span>{{ getPayGradeName(item) }}</span>
          <p class="text-carrotOrange text-xs">{{ getPayGradeRange(item) }}</p>
        </div>
        <span v-else>------</span>
      </span>
      <span v-else-if="item.category">
        {{ (item.data || item).category || "------" }}
      </span>
      <span class="capitalize" v-else-if="item.appointmentNature">
        {{ (item.data || item).appointmentNature || "------" }}
      </span>
      <span v-else-if="item.appointmentTenure">
        {{ (item.data || item).appointmentTenure || "------" }}
      </span>
      <span v-else-if="item.id">
        <Menu left top="-150" margin="24" class="my-2 p-0">
          <template v-slot:title>
            <icon icon-name="more_icon" size="xs" />
          </template>
          <div style="min-width: 180px; min-height: 81px" class="py-3 px-2">
            <div class="flex py-1 px-3 more h-8 cursor-pointer" @click=" $emit('edit', item.data.id)">
              <icon icon-name="edit" class-name="text-blueCrayola mr-2" size="xs" />
              <p class="pt-1">Edit</p>
            </div>
            <div class="flex px-3 py-1 more h-8 cursor-pointer" @click="$emit('delete', (item.data || item).id)">
              <icon icon-name="delete" class-name="text-desire mr-2" size="xsm" />
              <p class="pt-1">Delete</p>
            </div>
          </div>
        </Menu>
      </span>
    </template>
  </sTable>
</template>

<script>
import STable from "@scelloo/cloudenly-ui/src/components/table";
import Menu from "@/components/Menu";

export default {
  name: "NonPositionPanel",
  components: {
    STable,
    Menu,
  },
  props: {
    designationTitles: {
      type: Array,
      default: () => [],
    },
    activeTab: {
      type: String,
      default: "",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    compensationModel: {
      type: String,
      default: "",
    },
  },
  computed: {
    roleCompensationColumn() {
      if (this.compensationModel === "rangeBased") {
        return { title: "pay band", value: "payGrades" };
      }
      return { title: "pay scale", value: "payScales" };
    },
    tableHeaders() {
      if (this.activeTab === "Role") {
        return [
          { title: "title", value: "title" },
          { title: "job family", value: "jobClassification" },
          { title: "job level", value: "jobLevel" },
          { title: "job grade", value: "jobGrade" },
          this.roleCompensationColumn,
          { title: "", value: "id", image: true },
        ];
      }

      if (
        this.activeTab === "Post" ||
        this.activeTab === "Academic Post" ||
        this.activeTab === "Non-Academic Post"
      ) {
        return [
          { title: "Post title", value: "title" },
          { title: "cadres", value: "jobClassification" },
          { title: "grade level", value: "minimumJobLevel" },
          { title: "minimum step", value: "minimumJobGrade" },
          { title: "pay scale", value: "payScales" },
          { title: "", value: "id", image: true },
        ];
      }

      if (this.activeTab === "Administrative Post") {
        return [
          { title: "Post title", value: "title" },
          { title: "Role category", value: "category" },
          { title: "Appointment nature", value: "appointmentNature" },
          { title: "Appointment tenure", value: "appointmentTenure" },
          { title: "", value: "id", image: true },
        ];
      }

      return [];
    },
  },
  methods: {
    getRowData(item) {
      return item.data || item;
    },
    getFirstPayScale(item) {
      const jobScales = this.getRowData(item).payScales?.jobScales;
      return jobScales?.length ? jobScales[0] : null;
    },
    getPayScaleName(item) {
      const payScale = this.getFirstPayScale(item);
      if (!payScale) {
        return "------";
      }

      return payScale.name || "------";
    },
    getPayScaleAmount(item) {
      const payScale = this.getFirstPayScale(item);
      if (!payScale) {
        return "------";
      }

      const amount =
        payScale.payScaleAmount ??
        payScale.stepAmount ??
        payScale.rangeMinimum;

      if (amount == null || amount === "") {
        return "------";
      }

      return this.convertToCurrency(amount);
    },
    getFirstPayGrade(item) {
      const paygrades = this.getRowData(item).payGrades?.paygrades;
      return paygrades?.length ? paygrades[0] : null;
    },
    getPayGradeName(item) {
      const payGrade = this.getFirstPayGrade(item);
      if (!payGrade) {
        return "------";
      }

      return payGrade.name || payGrade.paygradeId || "------";
    },
    getPayGradeRange(item) {
      const payGrade = this.getFirstPayGrade(item);
      if (!payGrade) {
        return "------";
      }

      const { rangeMinimum, rangeMaximum } = payGrade;

      if (rangeMinimum != null && rangeMaximum != null) {
        return `${this.convertToCurrency(rangeMinimum)} - ${this.convertToCurrency(rangeMaximum)}`;
      }

      if (rangeMinimum != null) {
        return this.convertToCurrency(rangeMinimum);
      }

      if (rangeMaximum != null) {
        return this.convertToCurrency(rangeMaximum);
      }

      return "------";
    },
  },
};
</script>

